import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import SupabaseAuthBlock from "@/components/app/SupabaseAuthBlock";
import GoogleAuthButton from "@/components/app/GoogleAuthButton";
import { apiError } from "@/lib/apiError";
import { appUrl, marketingUrl } from "@/lib/domains";

const HERO = "https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxjYWxtJTIwZGVudGFsJTIwcGF0aWVudCUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3ODM0MTk3NzR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const nav = useNavigate();
  const { login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    // Defensive: trim whitespace autofill/paste often adds.  A single
    // leading / trailing space is the #1 cause of "works on my laptop,
    // fails on hers" auth bugs.
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();

    if (!cleanEmail || !cleanPassword) {
      const msg = "Please enter both your email and password.";
      setErrorMsg(msg);
      toast.error(msg);
      return;
    }

    try {
      await login(cleanEmail, cleanPassword);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      // Never render an object as a React child — always coerce to string.
      const msg = apiError(err, "Login failed. Please check your email and password.");
      // Log status + safe payload shape for support (no password values).
      // eslint-disable-next-line no-console
      console.warn("[login] failed", {
        status: err?.response?.status,
        detailType: Array.isArray(err?.response?.data?.detail)
          ? "array"
          : typeof err?.response?.data?.detail,
      });
      setErrorMsg(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <SEO title="Sign in" path="/login" description="Sign in to Viyora — your AI-powered dental clinic management workspace." />
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Clinic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-pine/60 via-pine/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-linen">
          <a href={marketingUrl("/")} data-testid="brand-link" className="flex items-center gap-2">
            <img src="/viyora-icon-64.png" alt="Viyora" className="w-9 h-9 " />
            <span className="font-display text-xl">Viyora</span>
          </a>
          <div>
            <div className="overline text-linen/80">Welcome</div>
            <div className="mt-3 font-display text-4xl leading-tight max-w-md">Quiet interfaces, loud results.</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 bg-linen">
        <div className="w-full max-w-sm">
          <form onSubmit={submit} data-testid="login-form" noValidate>
            <span className="overline text-ink-soft">Sign in</span>
            <h1 className="mt-3 font-display text-4xl tracking-tight">Welcome back.</h1>
            <p className="mt-2 text-sm text-ink-mid">Sign in with Google or use your email &amp; password.</p>

            <div className="mt-8">
              <GoogleAuthButton />
            </div>

            <div className="flex items-center gap-3 text-xs text-ink-soft my-6">
              <div className="flex-1 h-px bg-[#E8EAE9]" /> or continue with email <div className="flex-1 h-px bg-[#E8EAE9]" />
            </div>

            {errorMsg && (
              <div
                role="alert"
                data-testid="login-error"
                className="mb-4 rounded-xl border border-[#B94A48]/30 bg-[#FEEEEE] text-[#8A2E2C] px-4 py-3 text-sm flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-ink/80">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => setEmail(e.target.value.trim())}
                  className="w-full mt-1 rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-ink/80">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1 rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
                  required
                />
              </div>
            </div>

            <button data-testid="login-submit" type="submit" disabled={loading} className="mt-8 w-full btn-primary flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>

            <p className="mt-4 text-sm text-center">
              <Link to="/forgot-password" data-testid="link-forgot-password" className="text-ink-mid hover:text-ink hover:underline">
                Forgot password?
              </Link>
            </p>

            <p className="mt-4 text-sm text-ink-mid text-center">
              New here? <a href={appUrl("/signup")} data-testid="link-signup" className="text-ink font-medium hover:underline">Create an account</a>
            </p>
          </form>

          <SupabaseAuthBlock mode="login" />
        </div>
      </div>
    </div>
  );
}
